import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Sparkles, Shield, Volume2, VolumeX, RotateCcw, MapPin } from 'lucide-react';
import { heroData, doctifyData } from '../data/mock';
import { trackEvent } from '../lib/track';
import { videos } from '../data/videos';
import { useBookingModal } from '../context/BookingContext';
import { usePractice } from '../context/PracticeContext';
import { openDaysSummary } from '../data/practice';
import NextSlotWidget from './NextSlotWidget';
import './next-slot.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_VIDEO_ID = videos.aboutKrina.youtubeId;

const Hero = () => {
  const [googleRating, setGoogleRating] = useState(null);
  const [isMuted, setIsMuted] = useState(true);
  const [attentionPulse, setAttentionPulse] = useState(true);
  const iframeRef = useRef(null);
  const heroRef = useRef(null);
  const scrollPastFiredRef = useRef(false);
  const { openBooking } = useBookingModal();
  const { hours } = usePractice();

  // Fire a GA4 event when the visitor scrolls past the hero (one-shot per
  // page load). Lets the admin dashboard compute the % of visitors who never
  // engage with the above-the-fold CTAs.
  useEffect(() => {
    const node = heroRef.current;
    if (!node || !window.gtag) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting && entry.boundingClientRect.top < 0 && !scrollPastFiredRef.current) {
          scrollPastFiredRef.current = true;
          window.gtag('event', 'hero_scroll_past', {
            event_category: 'engagement',
            event_label: 'home_hero',
          });
        }
      },
      { threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // First-time visitor hint: pulse the "Meet Krina" mute pill for ~4s on mount,
  // then settle. Stops earlier if the user interacts with the toggle.
  useEffect(() => {
    const t = setTimeout(() => setAttentionPulse(false), 4500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/google-rating`).then((res) => {
      if (!cancelled && res.data?.rating != null) {
        setGoogleRating({
          rating: res.data.rating,
          count: res.data.user_rating_count,
          mapsUri: res.data.google_maps_uri,
        });
      }
    }).catch(() => { /* fall back silently to hardcoded value */ });
    return () => { cancelled = true; };
  }, []);

  const trackCta = (eventName) => {
    if (window.gtag) {
      window.gtag('event', eventName, {
        event_category: 'conversion',
        event_label: 'home_hero',
      });
    }
  };

  const handleWhatsApp = () => {
    trackCta('hero_whatsapp_click');
    trackEvent('whatsapp_click', 'hero');
    window.open(`https://wa.me/${heroData.whatsappNumber}?text=Hello! I'd like to book a consultation`, '_blank');
  };

  const handleBooking = () => {
    trackCta('hero_book_click');
    openBooking();
  };

  // Inject the live Google rating into the stat tile marked with `liveGoogle: true`.
  // Falls back to the static placeholder if the API is briefly unreachable.
  const stats = React.useMemo(() => {
    const open = openDaysSummary(hours);
    return heroData.stats.map((s) => {
      if (s.liveHours) return { ...s, value: String(open.count), sublabel: open.label };
      if (!googleRating) return s;
      return s.liveGoogle
        ? {
            ...s,
            value: `${googleRating.rating.toFixed(1)}★`,
            sublabel: `${googleRating.count} reviews`,
            link: googleRating.mapsUri,
          }
        : s;
    });
  }, [googleRating, hours]);

  // Safe, well-supported YouTube embed params for muted autoplay with looping:
  //  autoplay=1   — start playing immediately (only works with mute=1 per browser policy)
  //  mute=1       — required for browser autoplay
  //  loop=1 + playlist=ID  — loop a single video (the `playlist` param is YouTube's required mechanism)
  //  rel=0        — never show related videos
  //  modestbranding=1 — minimal YouTube branding
  //  playsinline=1 — keeps the video inline on iOS instead of fullscreening
  //  enablejsapi=1 — lets us send postMessage commands to mute/unmute from our overlay button
  //  controls=0   — hide YouTube's bottom playback bar (we use our own controls)
  //  iv_load_policy=3 — hide annotations
  //  disablekb=1  — disable keyboard shortcuts
  //  fs=0         — disable fullscreen
  //  showinfo=0   — legacy hide-title param (still respected by some clients)
  // A transparent ".hero-video-shield" overlay sits on top of the iframe to swallow
  // hover events so YouTube's hover overlays (title pill, watch-on-YT link, etc.) never appear.
  const videoSrc = `https://www.youtube.com/embed/${HERO_VIDEO_ID}?autoplay=1&mute=1&loop=1&playlist=${HERO_VIDEO_ID}&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&controls=0&iv_load_policy=3&disablekb=1&fs=0&showinfo=0`;

  const sendIframeCommand = (func, args = '') => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.postMessage(
      JSON.stringify({ event: 'command', func, args }),
      '*'
    );
  };

  const toggleMute = () => {
    setAttentionPulse(false); // user has seen the affordance — stop pulsing
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    sendIframeCommand(nextMuted ? 'mute' : 'unMute');
  };

  const handleRestart = () => {
    sendIframeCommand('seekTo', [0, true]);
    sendIframeCommand('playVideo');
  };

  return (
    <section className="hero-section" ref={heroRef}>
      <div className="hero-content">
        <div className="hero-text">
          <a
            href="#location"
            className="hero-badge hero-badge-link"
            data-testid="hero-location-badge"
            aria-label="Now at Waterloo, London — see location details"
            onClick={(e) => {
              const el = document.getElementById('location');
              if (!el) return;
              e.preventDefault();
              el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              trackEvent('hero_location_badge_click');
            }}
          >
            <MapPin size={15} strokeWidth={2.25} aria-hidden="true" />
            Now at Waterloo, London
          </a>
          <h1 className="hero-title">
            Expert Relief for
            <span className="hero-title-secondary">TMD, Tinnitus, Headaches &amp; Migraines</span>
          </h1>
          <div className="hero-trust-ribbon" data-testid="hero-trust-ribbon" aria-label="Clinic trust indicators">
            <span className="hero-trust-item" data-testid="hero-trust-google">
              <span className="hero-trust-star" aria-hidden="true">★</span>
              <span className="hero-trust-strong">{googleRating ? googleRating.rating.toFixed(1) : '4.9'}</span>
              <span className="hero-trust-muted">on Google</span>
              {googleRating?.count ? (
                <span className="hero-trust-muted hero-trust-review-count">· {googleRating.count} reviews</span>
              ) : null}
            </span>
            <span className="hero-trust-sep" aria-hidden="true">·</span>
            <span className="hero-trust-item">
              <span className="hero-trust-strong">Waterloo</span>
              <span className="hero-trust-muted">London SE1</span>
            </span>
            <span className="hero-trust-sep" aria-hidden="true">·</span>
            <span className="hero-trust-item">
              <span className="hero-trust-strong">HCPC-registered</span>
              <span className="hero-trust-muted">specialist physio</span>
            </span>
          </div>
          <p className="hero-description">
            Specialist physiotherapy treatment that addresses the root cause of your pain. Evidence-based care from Krina Panchal, TMD specialist with 15+ years experience.
          </p>

          <div className="hero-cta">
            <Button 
              className="cta-primary"
              onClick={handleBooking}
              data-testid="hero-book-consultation-btn"
            >
              Book Consultation
            </Button>
            <Button 
              className="cta-secondary"
              onClick={handleWhatsApp}
            >
              WhatsApp Us
            </Button>
            <div className="hero-next-slot" data-testid="hero-next-slot">
              <NextSlotWidget variant="hero" source="homepage-hero" />
            </div>
          </div>

          <div className="hero-trust-row" data-testid="hero-trust-row">
            {stats.map((stat) => {
              const inner = (
                <>
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                  {stat.sublabel ? <div className="stat-sublabel" data-testid="google-review-count">{stat.sublabel}</div> : null}
                </>
              );
              if (stat.scrollTo) {
                return (
                  <button
                    type="button"
                    key={stat.label}
                    className="stat-item stat-item-link"
                    onClick={() => {
                      const el = document.querySelector(stat.scrollTo);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    data-testid="hero-stat-scroll-location"
                  >
                    {inner}
                  </button>
                );
              }
              if (stat.link) {
                return (
                  <a
                    key={stat.label}
                    className="stat-item stat-item-link"
                    href={stat.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hero-google-rating-link"
                  >
                    {inner}
                  </a>
                );
              }
              return (
                <div key={stat.label} className="stat-item">
                  {inner}
                </div>
              );
            })}

            {googleRating ? (
              <a
                href={googleRating.mapsUri}
                target="_blank"
                rel="noopener noreferrer"
                className="hero-google-reviews-btn"
                data-testid="hero-google-reviews-btn"
                aria-label={`See all ${googleRating.count} Google reviews (opens in new tab)`}
              >
                <svg
                  className="hero-google-reviews-glyph"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  aria-hidden="true"
                >
                  <path
                    fill="#4285F4"
                    d="M22.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.3h5.9a5.05 5.05 0 0 1-2.19 3.31v2.75h3.54c2.07-1.9 3.26-4.7 3.26-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.96 0 5.45-.98 7.26-2.66l-3.54-2.75c-.98.66-2.23 1.06-3.72 1.06-2.86 0-5.28-1.93-6.14-4.53H2.21v2.84A11 11 0 0 0 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.86 14.12A6.6 6.6 0 0 1 5.5 12c0-.74.13-1.45.36-2.12V7.04H2.21A11 11 0 0 0 1 12c0 1.78.43 3.46 1.21 4.96l3.65-2.84z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.21 7.04l3.65 2.84C6.72 7.27 9.14 5.38 12 5.38z"
                  />
                </svg>
                <span>{googleRating.count} reviews on Google</span>
              </a>
            ) : null}
            <a
              href={doctifyData.url}
              target="_blank"
              rel="noopener noreferrer"
              className="hero-google-reviews-btn hero-doctify-reviews-btn"
              data-testid="hero-doctify-reviews-btn"
              aria-label={`Rated ${doctifyData.rating} stars on Doctify (opens in new tab)`}
            >
              <img
                src="/logos/doctify.jpg"
                alt="Doctify"
                className="hero-doctify-logo"
                loading="lazy"
              />
              <span>
                <strong>{doctifyData.rating.toFixed(1)}</strong>
                <span className="hero-doctify-star" aria-hidden="true"> ★</span>
              </span>
            </a>
          </div>
        </div>

        <div className="hero-carousel-wrapper" data-testid="hero-video-wrapper">
          <div className="hero-video-frame">
            <iframe
              ref={iframeRef}
              className="hero-video-iframe"
              src={videoSrc}
              title={videos.aboutKrina.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              data-testid="hero-video-iframe"
            />
            {/* Transparent shield: swallows pointer events so YouTube's hover-only
                overlays (title pill, "watch on YT" link, share buttons) never appear. */}
            <div className="hero-video-shield" aria-hidden="true" />
            {/* Custom controls layered on top of the shield */}
            <div className="hero-video-controls">
              <button
                type="button"
                className={`hero-video-control hero-video-mute ${isMuted ? 'is-muted' : 'is-unmuted'} ${attentionPulse ? 'has-attention-pulse' : ''}`}
                onClick={toggleMute}
                aria-label={isMuted ? 'Unmute Krina introduction video' : 'Mute Krina introduction video'}
                aria-pressed={!isMuted}
                data-testid="hero-mute-toggle"
              >
                {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                <span className="hero-video-control-label">{isMuted ? 'Unmute' : 'Mute'}</span>
              </button>
              <button
                type="button"
                className="hero-video-control hero-video-restart"
                onClick={handleRestart}
                aria-label="Restart Krina introduction video from the beginning"
                data-testid="hero-restart-btn"
                title="Restart video"
              >
                <RotateCcw size={14} />
              </button>
            </div>
          </div>

          {/* Trust + feature row — sits directly below the video on desktop,
              re-flows into the natural stack on mobile. */}
          <div className="hero-credibility-card" data-testid="hero-clinically-validated">
            <div className="hero-credibility-icon">
              <Shield size={22} style={{ color: 'var(--color-primary)' }} />
            </div>
            <div className="hero-credibility-body">
              <div className="hero-credibility-text">
                <strong>Clinically Validated</strong>
                <span>Evidence-based treatment trusted by patients across London, the UK and visitors from mainland Europe.</span>
              </div>
              <div className="hero-credibility-proof">
                {googleRating && googleRating.rating ? (
                  <a
                    href={googleRating.mapsUri || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hero-credibility-chip hero-credibility-chip-rating"
                    aria-label={`Google rating ${googleRating.rating} stars from ${googleRating.count} reviews`}
                    data-testid="hero-credibility-rating-chip"
                  >
                    <span className="hero-credibility-star" aria-hidden="true">★</span>
                    <strong>{Number(googleRating.rating).toFixed(1)}</strong>
                    <span className="hero-credibility-chip-meta">{googleRating.count} Google reviews</span>
                  </a>
                ) : null}
                <span className="hero-credibility-chip hero-credibility-chip-muted">
                  <strong>1,000+</strong> patients treated
                </span>
              </div>
            </div>
          </div>

          <div className="hero-features">
            <div className="hero-feature-item">
              <Sparkles className="w-5 h-5" />
              <span>Personalized care</span>
            </div>
            <div className="hero-feature-item">
              <Shield className="w-5 h-5" />
              <span>Proven results</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
